import { useCallback, useEffect, useMemo, useState } from "react";

import { ConfidenceSlider } from "@rapid/components/ConfidenceSlider";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
import { useCurrentMedia } from "@rapid/store";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { workflowAcceptsConfidence } from "@rapid/utils/workflowCapabilities";

export function ConfidenceFilterCard() {
    const currentMedia = useCurrentMedia();
    const { currentWorkflow } = useCurrentWorkflow();
    const { currentFramePredictions } = useComputedValues();
    const { updateMediaItem } = useMediaUtils();
    const [confidenceThreshold, setConfidenceThreshold] = useState(
        currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD
    );

    useEffect(() => {
        setConfidenceThreshold(
            currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD
        );
    }, [currentMedia?.file.confidenceThreshold, currentMedia?.file.id]);

    const updateConfidenceThreshold = useCallback(
        (newThreshold: number) => {
            if (!currentMedia?.file) return;

            setConfidenceThreshold(newThreshold);
            updateMediaItem({ ...currentMedia.file, confidenceThreshold: newThreshold });
        },
        [currentMedia?.file, updateMediaItem]
    );

    const visiblePredictionCount = useMemo(() => {
        if (!Array.isArray(currentFramePredictions)) return 0;
        return currentFramePredictions.filter(
            (prediction) => prediction.confidence >= confidenceThreshold
        ).length;
    }, [currentFramePredictions, confidenceThreshold]);

    if (!currentMedia?.file || !workflowAcceptsConfidence(currentWorkflow)) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-lg border border-gray-300 bg-gray-50">
            <ConfidenceSlider
                className="gap-2 px-4 py-2.5"
                initialConfidenceThreshold={confidenceThreshold}
                onConfidenceThresholdChanged={updateConfidenceThreshold}
                predictionCount={visiblePredictionCount}
                showCount={false}
            />
        </div>
    );
}
